<script setup lang="ts">
import TokenSearchInput from '@/components/inputs/TokenSearchInput.vue';
import sum from 'lodash/sum';
import chunk from 'lodash/chunk';
import reduce from 'lodash/reduce';
import extend from 'lodash/extend';
import values from 'lodash/values';
import PoolsTable from '@/components/tables/PoolsTable/PoolsTable.vue';
import usePoolFilters from '@/composables/pools/usePoolFilters';
import useNetwork from '@/composables/useNetwork';
import { lsSet } from '@/lib/utils';
import LS_KEYS from '@/constants/local-storage.keys';
import { ref } from 'vue';
import { useGetPoolEvents } from '@/services/api/pool.service';
import { UPoolMulticaller } from '@/services/pool/decorators/upool.multicaller';
import { toHumanReadableNumber } from '@/lib/utils/numbers';
import { SliceZone, usePrismicDocumentByUID } from '@prismicio/vue';
import { components } from '@/slices';
import PoolsGrid from '@/components/contextual/pages/pools/PoolsGrid/PoolsGrid.vue';
import EmptyPools from '@/components/contextual/pages/pools/EmptyPools.vue';
import useUIDCheck from '@/composables/useUIDCheck';
import { DEMO_POOL_IDS, DEMO_VAULT_IDS } from '@/constants/pools';
import { useGetVaultEvents } from '@/services/api/vault.service';
import { filter } from 'lodash';

useUIDCheck();

/**
 * STATE
 */
const route = useRoute();
const urlSortParam = route.query?.sort as string | undefined;
// const initSortCol = urlSortParam || lsGet(LS_KEYS.App.PoolSorting) || 'id';
const initSortCol = urlSortParam || 'id';

/**
 * COMPOSABLES
 */
const { selectedTokens, addSelectedToken, removeSelectedToken } =
  usePoolFilters();
const { networkConfig, networkId } = useNetwork();
const { data, isLoading } = useGetPoolEvents(); // data here is a list of poolNotes
const { data: vaultListData, isLoading: vaultListIsLoading } =
  useGetVaultEvents(); // data here is a list of poolNotes
console.log('VERSION_9');
/**
 * COMPUTED
 */

const allPools = computed(() =>
  data.value?.filter(x => {
    // For PROD, filter out demo pools
    if (import.meta.env.MODE === 'production') {
      return !DEMO_POOL_IDS.includes(x.pool.id);
    }
    const _isVault = x?.pool?.isVault;
    if (_isVault) {
      return false;
    }
    return true;
  })
);

const vaults = computed(() => {
  const filterVaultByChain = filter(vaultListData.value, x => {
    const _selectedNetworkId = networkId.value || 42220;
    if (x.chainId == _selectedNetworkId) {
      return true;
    } else {
      return false;
    }
  });
  if (networkId.value == 100) {
    filterVaultByChain.push({
      details: {
        name: 'USDn',
        symbol: 'USDyc',
        description: 'Delta-neutral Strategy',
        shortDescription: 'Delta-neutral Strategy',
        collateral: {
          published: {
            active: true,
            label: '',
            content: '',
            _id: '67bb45ead4d01f5ec7c254ff',
          },
          _id: '67d988c6c8bede8494605bae',
        },
        executiveSummary: {
          published: {
            active: true,
            label: '',
            content:
              "As the vault is non-custodial, the vault's curator can only execute predetermined transactions based on permission contract (To view permissions click then set chain to Base with contract 0xc0a648967A2c1c736CFE4A0Bb517F16135A8f3AA)",
            _id: '67bb45ead4d01f5ec7c25503',
          },
          _id: '67d988c6c8bede8494605baf',
        },
        headline: {
          published: {
            active: true,
            label: '',
            content: '',
            _id: '67bb45ead4d01f5ec7c25507',
          },
          _id: '67d988c6c8bede8494605bb0',
        },
        highlight1: {
          published: {
            active: true,
            label: '100% Assets-backed Stability',
            content:
              'USDyc is 100% backed by eligible collaterals, currently split between money market funds (both tokenized and native) and yield-acquiring stable coins.',
            _id: '67bb45ead4d01f5ec7c25506',
          },
          _id: '67d988c6c8bede8494605bb1',
        },
        highlight2: {
          published: {
            active: true,
            label: 'Non-custodial and automated',
            content:
              'Assets are held in non-custodial wallets (Safe) and auto-rebalanced by Credio, a risk oracle powered by ML models and zero-knowledge proof.',
            _id: '67bb45ead4d01f5ec7c25505',
          },
          _id: '67d988c6c8bede8494605bb2',
        },
        highlight3: {
          published: {
            active: true,
            label: 'Permissionless and composable',
            content:
              'USDyc is permissionless and freely composable in DeFi beyond the point of permissioned minting and burning.',
            _id: '67bb45ead4d01f5ec7c25504',
          },
          _id: '67d988c6c8bede8494605bb3',
        },
        poolCard: {
          published: {
            active: true,
            label: 'RWA-backed Liquid Staking Token',
            content:
              'USDyc is a RWA-backed liquid staking token designed as a highly composable liquidity solution that rewards users with ownership of the Untangled Protocol.',
            _id: '67bb45ead4d01f5ec7c25508',
          },
          _id: '67d988c6c8bede8494605bb4',
        },
        promo1: {
          published: {
            active: true,
            label: '',
            content: '',
            _id: '67bb45ead4d01f5ec7c25502',
          },
          _id: '67d988c6c8bede8494605bb5',
        },
        promo2: {
          published: {
            active: true,
            label: '',
            content: '',
            _id: '67bb45ead4d01f5ec7c25501',
          },
          _id: '67d988c6c8bede8494605bb6',
        },
        targetReturn: {
          published: {
            active: true,
            label: '',
            content: '',
            _id: '67bb45ead4d01f5ec7c25500',
          },
          _id: '67d988c6c8bede8494605bb7',
        },
      },
      _id: '67970aaeed4e65b24adbd9af',
      vaultId: 1,
      vaultAddress: '9kwoKmpNtPEK4fESLf7YnrGtZqvHcnYoN7aVWWdvkhSP',
      treasury: '0x53B4dBdbc9861A626C62DE557ED4f1560FFa3822',
      chainId: 100,
      underlyingAssetAddress: '0xcebA9300f2b948710d2653dD7B07f33A8B32118C',
      isOpen: true,
      __v: 0,
      issuerId: 2,
      underlyingAssetDecimals: 6,
      underlyingAssetSymbol: 'USDC',
      createdAt: '2025-01-01T00:00:00.000Z',
      aggregateId: 1,
    });
  }
  return filterVaultByChain.filter(x => {
    // if (x.chainId == 137) {
    //   return false;
    // }

    if (import.meta.env.MODE === 'production') {
      return !DEMO_VAULT_IDS.includes(x.id);
    }
    return true;
  });
});

const poolsSortField = ref('totalLiquidity');
const page = ref(0);
const nextPage = function () {
  page.value = page.value + 1;
};
let pools = computed(() =>
  allPools.value ? allPools.value.slice(0, page.value * 10 + 10) : []
);

const shouldShowActivePools = computed(() => {
  return (allPools?.value?.length || 0) > 0;
});

const isPaginated = computed(() => {
  return page.value * 10 + 10 <= (allPools?.value?.length || 0);
});

const { data: sliceData } = usePrismicDocumentByUID('index', 'promo');

const poolValues = ref({});
watchEffect(async () => {
  if (!allPools?.value?.length) {
    return;
  }
  const poolArrays = chunk(allPools.value, 2);
  const poolMulticallers = poolArrays.map(a =>
    new UPoolMulticaller().fetchPoolValues(a)
  );
  const resultList = await Promise.all(poolMulticallers);
  const result: any = reduce(resultList, extend);

  poolValues.value = result;
});

const poolValueByPoolId = computed(() => {
  const poolValueMap = {} as Record<string, number>;

  pools.value.forEach(p => {
    const pValues = values(poolValues.value[p.pool.id]).map(e => {
      return e
        ? toHumanReadableNumber(e.toString(), p.pool.currency.decimals)
        : 0;
    });
    const pValue = sum(pValues);
    poolValueMap[p.pool.id] = pValue;
  });

  return poolValueMap;
});

/**
 * METHODS
 */
function onColumnSort(columnId: string) {
  poolsSortField.value = columnId;
  lsSet(LS_KEYS.App.PoolSorting, columnId);
}
</script>

<template>
  <div>
    <template v-if="sliceData">
      <SliceZone
        :slices="[sliceData.data.slices?.[0]]"
        :components="components"
      />
    </template>
    <div class="xl:container xl:px-4 pt-10 md:pt-8 xl:mx-auto">
      <template v-if="sliceData">
        <SliceZone
          :slices="[sliceData.data.slices?.[1]]"
          :components="components"
        />
      </template>
      <BalStack vertical>
        <div class="px-4 xl:px-0">
          <div class="flex justify-between items-end mb-2">
            <h3>All Vaults</h3>
          </div>

          <div class="flex justify-between items-center w-full">
            <TokenSearchInput
              v-model="selectedTokens"
              class="w-full md:w-2/3"
              @add="addSelectedToken"
              @remove="removeSelectedToken"
            />

            <!--            <SwitchTableViewButton />-->
          </div>
        </div>

        <div class="px-4 xl:px-0">
          <EmptyPools v-if="!vaultListIsLoading && !vaults?.length" />
          <PoolsGrid
            :poolNotes="vaults || []"
            :poolValueByPoolId="poolValueByPoolId"
            :isLoading="vaultListIsLoading"
          />
        </div>

        <div v-if="shouldShowActivePools" class="px-4 xl:px-0">
          <div class="flex justify-between items-end mb-2">
            <h3>All pools</h3>
          </div>

          <div class="flex justify-between items-center w-full">
            <TokenSearchInput
              v-model="selectedTokens"
              class="w-full md:w-2/3"
              @add="addSelectedToken"
              @remove="removeSelectedToken"
            />

            <!--            <SwitchTableViewButton />-->
          </div>
        </div>

        <div v-if="shouldShowActivePools" class="px-4 xl:px-0">
          <EmptyPools v-if="!isLoading && !allPools?.length" />
          <PoolsTable
            :data="allPools"
            :poolValueByPoolId="poolValueByPoolId"
            :noPoolsLabel="$t('noPoolsFound')"
            :isLoading="isLoading"
            :selectedTokens="selectedTokens"
            class="mb-8"
            :sortColumn="initSortCol"
            :hiddenColumns="['migrate', 'actions', 'lockEndDate']"
            :isLoadingMore="false"
            :isPaginated="isPaginated"
            skeletonClass="pools-table-loading-height"
            @on-column-sort="onColumnSort"
            @load-more="nextPage"
          />
        </div>

        <div v-if="sliceData?.data?.slices?.[2]" class="p-4 xl:p-0 mt-4">
          <SliceZone
            :slices="[sliceData.data.slices?.[2]]"
            :components="components"
          />
        </div>
      </BalStack>
    </div>
  </div>
</template>

<style>
.pools-table-loading-height {
  height: 40rem;
}
</style>
